// import React, { Suspense } from "react";
// import { Navigate, Route, Routes, useLocation } from "react-router-dom";
// import PageLoader from "../components/Loader/PageLoader";

// // Lazy-loaded components
// const LoginIndex = React.lazy(() => import("../Pages/Auth/LoginIndex.jsx"));
// const SignUpIndex = React.lazy(() => import("../Pages/Auth/SignUpIndex.jsx"));
// const UserAllRoutes = React.lazy(() => import("./UserAllRoutes.jsx"));
// const Error404 = React.lazy(() => import("../components/Error404.jsx"));

// function RedirectIfUndefined() {
//   const location = useLocation();
//   if (location.pathname.includes("undefined")) {
//     return <Navigate to="/" replace />;
//   }
//   return null;
// }

// function AllRoutes({ props }) {
//   const { currentTheme, TranslateCheckOut, lang } = props;
//   const langPrefix = TranslateCheckOut?.translate_checkout ? `/:lang` : "";


//   const location = useLocation();

//   return (
//     <Suspense
//     // fallback={<PageLoader />}
//     >
//       <RedirectIfUndefined />
//       <Routes>
//         {/* ✅ Handle '/' route properly based on translation setting */}
//         <Route
//           path="/"
//           element={
//             TranslateCheckOut?.translate_checkout && location.pathname === "/" ? (
//               <Navigate to={`/${lang}`} replace />
//             ) : (
//               <UserAllRoutes props={{ currentTheme }} />
//             )
//           }
//         />

//         {/* ✅ Route to /login will redirect to /:lang/login if translation is enabled */}
//         <Route
//           path="/login"
//           element={
//             TranslateCheckOut?.translate_checkout ? (
//               <Navigate to={`/${lang}/login`} replace />
//             ) : (
//               <LoginIndex />
//             )
//           }
//         />

//         <Route
//           path="/signup"
//           element={
//             TranslateCheckOut?.translate_checkout ? (
//               <Navigate to={`/${lang}/signup`} replace />
//             ) : (
//               <SignUpIndex />
//             )
//           }
//         />

//         {/* ✅ Lang-prefixed routes */}
//         {TranslateCheckOut?.translate_checkout && (
//           <>
//             <Route path="/:lang/login" element={<LoginIndex />} />
//             <Route path="/:lang/signup" element={<SignUpIndex />} />
//           </>
//         )}

//         {/* ✅ Protected routes */}
//         <Route
//           path={`${langPrefix}/*`}
//           element={<UserAllRoutes props={{ currentTheme }} />}
//         />

//         {/* ✅ 404 Catch All */}
//         <Route path="*" element={<Error404 />} />
//       </Routes>
//     </Suspense>
//   );
// }

// export default AllRoutes;



import React, { Suspense, useEffect } from "react";
import { Navigate, Route, Routes, useLocation, useParams, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import PageLoader from "../components/Loader/PageLoader";
import { getItem } from "../Utils/localStorageUtils";
import { jwtDecode } from "jwt-decode"
import LandingPageFront from "../Pages/LandingPage/LandingPageFront.jsx";

// Lazy-loaded components
import { lazyWithRetry } from "../Utils/chunkRetryUtils";

const LoginIndex = lazyWithRetry(() => import("../Pages/Auth/LoginIndex.jsx"));
const SignUpIndex = lazyWithRetry(() => import("../Pages/Auth/SignUpIndex.jsx"));
const DeviceRestriction = lazyWithRetry(() => import("../Pages/Auth/DeviceRestriction.jsx"));
const UserAllRoutes = lazyWithRetry(() => import("./UserAllRoutes.jsx"));
const LandingPage = lazyWithRetry(() => import("../Pages/LandingPage/LandingPage.jsx"));
const Error404 = lazyWithRetry(() => import("../components/Error404.jsx"));



const DeepLinkHandler = () => {
  const { token, plan_id, type, slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (token) {
      try {
        const queryParams = new URLSearchParams(location.search);
        // The user wants the scheme to come from ?token=... in their new concept
        const returnScheme = queryParams.get('token');

        // 1. Decode the token to get user details
        const decoded = jwtDecode(token);
        
        // 2. Overwrite old token and set session data
        // Explicitly setting as string to avoid JSON.stringify adding extra quotes
        localStorage.setItem('access_token', token);
        if (decoded.id) localStorage.setItem('user_id', String(decoded.id));
        if (decoded.email) localStorage.setItem('email', String(decoded.email));
        if (decoded.role) localStorage.setItem('role', String(decoded.role));

        // 3. Store the dynamic return scheme if provided
        if (returnScheme) {
          localStorage.setItem('return_scheme', returnScheme);
        } else {
          localStorage.removeItem('return_scheme');
        }

        // 4. Store plan_id if available to auto-select it later
        if (plan_id) {
          localStorage.setItem('deep_link_plan_id', plan_id);
        } else {
          localStorage.removeItem('deep_link_plan_id');
        }

        // 5. Set expiration timestamp
        const expiresInParam = queryParams.get('expires_in');
        const expiresInSeconds = expiresInParam ? parseInt(expiresInParam) : 600; // Default 10 mins
        const expiry = Date.now() + expiresInSeconds * 1000;
        localStorage.setItem('access_token_expiry', String(expiry));

        // 6. Redirect to the plan page or PPV page
        if (plan_id || !type) {
          navigate('/plan', { replace: true });
        } else if (type && slug) {
          localStorage.setItem('deep_link_action', 'ppv');
          localStorage.setItem('deep_link_source_type', type);
          localStorage.setItem('deep_link_slug', slug);
          navigate(`/${type}/${slug}`, { replace: true });
        }
      } catch (error) {
        console.error("Invalid deep link token:", error);
        navigate('/', { replace: true });
      }
    }
  }, [token, plan_id, navigate, location]);

  return <PageLoader />;
};

function RedirectIfUndefined() {
  const location = useLocation();
  if (location.pathname.includes("undefined")) {
    return <Navigate to="/" replace />;
  }
  return null;
}

function AllRoutes({ props, settings, siteTheme }) {
  const { currentTheme, TranslateCheckOut, lang, landingpageData } = props;
  const location = useLocation();
  const isUserLoggedIn = !!(localStorage.getItem("access_token") || sessionStorage.getItem("access_token"));
  const langPrefix = Number(TranslateCheckOut?.translate_checkout) === 1 ? `/:lang` : "";

  // Check if user is authenticated
  const token = getItem('access_token');
  const decoded = token ? jwtDecode(token) : null;

  const isAuthenticated = !!token;
  const isDeviceRestricted = !!decoded?.isDeviceRestriction;

  // Provide fallback settings if not loaded to prevent infinite loading
  const fallbackSettings = settings || { enable_landing_page: "0", access_free: 1 };
  const settingsToUse = Object.keys(fallbackSettings).length > 0 ? fallbackSettings : { enable_landing_page: "0", access_free: 1 };

  const shouldShowLandingPage = Number(settingsToUse.enable_landing_page) === 1 && settingsToUse.access_free !== 1 && siteTheme?.movie_stripe == 1 && !isAuthenticated;
  const enableLandingPage = Number(settingsToUse.enable_landing_page) === 1 && settingsToUse.access_free !== 1 && !isAuthenticated;
  const isBarePath = location.pathname === '/' || location.pathname === '/login' || location.pathname === '/signup' || location.pathname === '/choose-profile' || location.pathname === '/plan';

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    // Reset scroll position to top on route change with a slight delay
    const timer = setTimeout(() => {
      const rootElement = document.getElementById('root');
      if (rootElement) {
        rootElement.scrollTo(0, 0);
      } else {
        window.scrollTo(0, 0);
      }
    }, 100);

    const authPaths = [
      '/login',
      '/signup',
      '/verify',
      '/forgot-password',
      '/device-restriction',
      '/choose-profile',
      '/plan'
    ];

    const currentPath = location.pathname;

    const isAuthPath = authPaths.some(path => currentPath.includes(path));

    if (!isAuthPath) {
      localStorage.setItem('redirect_after_login', currentPath + location.search);
    }

    return () => clearTimeout(timer);
  }, [location.pathname, location.search]);


  return (
    <Suspense
    // fallback={<PageLoader />}
    >
      <RedirectIfUndefined />
      <Routes>

        {!isAuthenticated && shouldShowLandingPage && <Route path="/" element={<LandingPage />} />}
        {!isAuthenticated && enableLandingPage && <Route path="/" element={<LandingPageFront />} />}
        {!isAuthenticated && enableLandingPage && <Route path={landingpageData?.slug} element={<LandingPageFront />} />}

        {/* Redirect to language-specific route if translation is enabled */}
        {Number(TranslateCheckOut?.translate_checkout) === 1 && isBarePath && (
          <>
            <Route path="/" element={<Navigate to={`/${lang || 'engs'}`} replace />} />
            
            {/* Safety net: Redirect non-prefixed core paths to their prefixed versions */}
            <Route path="/login" element={<Navigate to={`/${lang || 'engs'}/login`} replace />} />
            <Route path="/signup" element={<Navigate to={`/${lang || 'engs'}/signup`} replace />} />
            <Route path="/choose-profile" element={<Navigate to={`/${lang || 'engs'}/choose-profile`} replace />} />
            <Route path="/plan" element={<Navigate to={`/${lang || 'engs'}/plan`} replace />} />
          </>
        )}


        {/* Authentication Routes */}
        <Route path={`${langPrefix}/signup`} element={<SignUpIndex />} />
        <Route path={`${langPrefix}/login`} element={<LoginIndex />} />

        {/* Device Restriction Route */}
        {isAuthenticated && isDeviceRestricted && (
          <Route path="/device-restriction" element={<DeviceRestriction />} />
        )}

        {/* Protected Routes */}
        <Route path={`${langPrefix}/*`} element={isAuthenticated && isDeviceRestricted ? <Navigate to="/device-restriction" replace /> : <UserAllRoutes props={{ currentTheme, siteTheme }} />} />

        {/* Deep Linking Routes */}
        <Route path="/deep-link/:token/plan" element={<DeepLinkHandler />} />
        <Route path="/deep-link/:token/:plan_id/plan" element={<DeepLinkHandler />} />
        <Route path="/deep-link/:token/ppv/:type/:slug" element={<DeepLinkHandler />} />

        {/* 404 Route (keep this last) */}
        {Number(siteTheme?.disable_404_screen) === 1 ? (
          <Route path="*" element={<Navigate to="/" replace />} />
        ) : (
          <Route path="*" element={<Error404 />} />
        )}
      </Routes>
    </Suspense>
  );
}

const mapStateToProps = state => ({
  settings: state.get_setting_Reducer.settings,
  siteTheme: state.get_siteTheme_Reducer.siteTheme
});

export default connect(mapStateToProps)(AllRoutes);
